/**
 * EntryModelListItem Component
 *
 * Displays a single entry model row with:
 * - Name
 * - Category badge (color swatch + name)
 * - "Used in" count with popover
 * - Action menu (Edit, Archive/Restore)
 */

import React from 'react';
import { MoreVertical } from 'lucide-react';

import {
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from '@enfomentis/ui';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';

import { UsedInPopover } from '../Criteria/UsedInPopover';

export interface EntryModelListItemProps {
  /** The entry model to display */
  entryModel: EntryModel;
  /** The category this entry model belongs to (if any) */
  category: EntryModelCategory | undefined;
  /** Array of playbook names that use this entry model */
  playbookNames: string[];
  /** Handler for edit action */
  onEdit: () => void;
  /** Handler for archive click (opens confirmation in parent) */
  onArchiveClick: () => void;
  /** Handler for restore action */
  onRestore: () => void;
}

export const EntryModelListItem: React.FC<EntryModelListItemProps> = ({
  entryModel,
  category,
  playbookNames,
  onEdit,
  onArchiveClick,
  onRestore,
}) => {
  const isArchived = entryModel.status === 'archived';

  return (
    <div
      className={`flex items-center gap-3 p-3 ${
        isArchived ? 'opacity-60 bg-default-50' : 'bg-content1'
      }`}
    >
      {/* Name + Archived Badge */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center min-w-0">
          <span
            className={`font-medium truncate ${
              isArchived ? 'text-text-tertiary' : 'text-text-primary'
            }`}
          >
            {entryModel.name}
          </span>
          {isArchived && (
            <Chip className="ml-2 shrink-0 text-xs text-text-tertiary bg-default-100">
              Archived
            </Chip>
          )}
        </div>
        {entryModel.description && (
          <span className="text-sm text-text-secondary truncate block">
            {entryModel.description}
          </span>
        )}
      </div>

      {/* Category Badge */}
      <div className="shrink-0">
        {category ? (
          <Chip
            size="sm"
            variant="flat"
            className="text-xs"
            style={{ backgroundColor: `${category.color}20`, color: category.color }}
            startContent={
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: category.color }}
              />
            }
          >
            {category.name}
          </Chip>
        ) : (
          <span className="text-xs text-text-tertiary">No category</span>
        )}
      </div>

      {/* Used In Count */}
      <div className="shrink-0">
        <UsedInPopover playbookNames={playbookNames} />
      </div>

      {/* Actions Menu */}
      <div className="shrink-0">
        <Dropdown>
          <DropdownTrigger>
            <Button
              size="sm"
              variant="light"
              isIconOnly
              aria-label="Entry model actions"
            >
              <MoreVertical size={16} />
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Entry model actions">
            <DropdownItem key="edit" onPress={onEdit}>
              Edit
            </DropdownItem>
            {isArchived ? (
              <DropdownItem key="restore" onPress={onRestore}>
                Restore
              </DropdownItem>
            ) : (
              <DropdownItem
                key="archive"
                onPress={onArchiveClick}
                className="text-warning-600"
              >
                Archive
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
};
